import { Transaction, TransactionStatus } from '@/types/transaction';
import { startOfDay, isBefore, isAfter, addDays, endOfDay } from 'date-fns';

/**
 * Calculate the dynamic status of a transaction based on dates and payment state
 * @param transaction - The transaction object
 * @param currentDate - Optional current date (for testing, defaults to today)
 * @returns The calculated transaction status
 */
export function calculateTransactionStatus(
  transaction: Pick<Transaction, 'is_paid' | 'due_date' | 'payment_date' | 'date'>,
  currentDate: Date = new Date()
): TransactionStatus {
  // If transaction is paid, it should always be PAID regardless of dates
  if (transaction.is_paid) {
    return 'PAID';
  }

  // Transaction is not paid - check due date
  const today = startOfDay(currentDate);

  // Use due_date if available, otherwise fall back to date or payment_date
  const effectiveDueDate = transaction.due_date
    ? new Date(transaction.due_date)
    : transaction.date
      ? new Date(transaction.date)
      : transaction.payment_date
        ? new Date(transaction.payment_date)
        : null;

  // If no due date available, default to PENDING
  if (!effectiveDueDate) {
    return 'PENDING';
  }

  const dueDate = startOfDay(effectiveDueDate);

  // Check if due date is in the past
  if (isBefore(dueDate, today)) {
    return 'OVERDUE';
  }

  // Default status for unpaid transactions
  return 'PENDING';
}

/**
 * Get the display status variant for the StatusPill component
 * @param transaction - The transaction object
 * @returns The status variant string for StatusPill
 */
export function getTransactionStatusVariant(
  transaction: Pick<Transaction, 'is_paid' | 'due_date' | 'payment_date' | 'date'>,
  currentDate: Date = new Date()
): string {
  const status = calculateTransactionStatus(transaction, currentDate);

  switch (status) {
    case 'PAID':
      return 'paid-standard';
    case 'OVERDUE':
      return 'overdue-standard';
    case 'PENDING':
      return 'pending';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'default';
  }
}

/**
 * Get the display status text in Portuguese
 * @param transaction - The transaction object
 * @returns The status text in Portuguese
 */
export function getTransactionStatusText(
  transaction: Pick<Transaction, 'is_paid' | 'due_date' | 'payment_date' | 'date'>,
  currentDate: Date = new Date()
): string {
  const status = calculateTransactionStatus(transaction, currentDate);

  switch (status) {
    case 'PAID':
      return 'Pago';
    case 'OVERDUE':
      return 'Vencido';
    case 'PENDING':
      return 'Pendente';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return 'Pendente';
  }
}

/**
 * Calculate the number of days a transaction is overdue
 * @param transaction - The transaction object
 * @param currentDate - Optional current date (for testing, defaults to today)
 * @returns Number of days overdue (0 if not overdue)
 */
export function calculateDaysOverdue(
  transaction: Pick<Transaction, 'is_paid' | 'due_date' | 'payment_date' | 'date'>,
  currentDate: Date = new Date()
): number {
  const status = calculateTransactionStatus(transaction, currentDate);

  if (status !== 'OVERDUE') {
    return 0;
  }

  const today = startOfDay(currentDate);

  // Use due_date if available, otherwise fall back to date or payment_date
  const effectiveDueDate = transaction.due_date
    ? new Date(transaction.due_date)
    : transaction.date
      ? new Date(transaction.date)
      : transaction.payment_date
        ? new Date(transaction.payment_date)
        : null;

  if (!effectiveDueDate) {
    return 0;
  }

  const dueDate = startOfDay(effectiveDueDate);

  // Calculate full days overdue
  return Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Update transaction with dynamic status calculation
 * @param transaction - The transaction object
 * @param currentDate - Optional current date (for testing, defaults to today)
 * @returns Transaction object with updated status
 */
export function updateTransactionStatus(
  transaction: Transaction,
  currentDate: Date = new Date()
): Transaction {
  const status = calculateTransactionStatus(transaction, currentDate);
  const daysOverdue = calculateDaysOverdue(transaction, currentDate);

  return {
    ...transaction,
    status,
    daysOverdue,
  };
}